import _ from 'lodash';
import {connect} from 'react-redux';

import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../../srcCommon/helpers/reselectHelpers';
import {
  isProviderConferenceType,
  mapIntegrationTypeToConferenceType,
} from '../../../../helpers/models/conferenceHelpers';
import {ResolveDispatchProps} from '../../../../helpers/reduxHelpers';
import {ConferenceTypesEnum} from '../../../../models/api/conferenceModel';
import {IntegrationModel} from '../../../../models/api/integrationModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../services/tracking';
import {AppState} from '../../../../store/appState';
import {ThunkAction} from '../../../../store/appThunk';
import {getConferencingIntegrations} from '../../../../store/data/dataSelectors';
import {getDefaultVideoConferencing} from '../../../../store/data/preferences/preferencesSelectors';
import {getIsUserGlobalAdmin} from '../../../../store/data/teammates/teammatesSelectors';
import {navigateToLinkAction} from '../../../../store/stateless/navigationActions';
import {ConferenceDropdownBox} from './conferenceDropdownBox';

/*
 * Props.
 */

interface ConferenceDropdownBoxContainerProps {
  supportedProviderConferenceTypes: ReadonlyArray<ConferenceTypesEnum>;
  onConferenceTypeChange: (
    conferenceType: ConferenceTypesEnum | undefined,
    integration: IntegrationModel | undefined,
  ) => void;
  calendarVersionId: number;
}

/*
 * Selectors.
 */

function getSupportedProviderConferenceTypes(_state: AppState, props: ConferenceDropdownBoxContainerProps) {
  return props.supportedProviderConferenceTypes;
}

function getCurrentCalendarVersionId(_state: AppState, props: ConferenceDropdownBoxContainerProps) {
  return props.calendarVersionId;
}

const getAvailableConferenceTypes = createSelector(
  [getConferencingIntegrations, getSupportedProviderConferenceTypes],
  (conferencingIntegrations, supportedProviderConferenceTypes) =>
    _(conferencingIntegrations)
      .map((i) => mapIntegrationTypeToConferenceType(i.type))
      .compact()
      .concat(supportedProviderConferenceTypes)
      .value(),
);

const getDefaultConferencingType = createSelector(
  [getDefaultVideoConferencing, getCurrentCalendarVersionId],
  (defaultVideoConferencing, currentCalendarVersionId) => defaultVideoConferencing[currentCalendarVersionId],
);

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ConferenceDropdownBoxContainerProps>()(() => ({
  conferencingIntegrations: getConferencingIntegrations,
  availableConferenceTypes: getAvailableConferenceTypes,
  isGlobalAdmin: getIsUserGlobalAdmin,
  defaultConferencingType: getDefaultConferencingType,
}));

const mapDispatchToProps = makeMapDispatchToProps<ConferenceDropdownBoxContainerProps>()(() => ({
  onGoToIntegrationSettingsClick: (): ThunkAction => (dispatch, getState) => {
    if (!getIsUserGlobalAdmin(getState())) {
      return;
    }

    // TODO: Update this link to go to integrations page instead of Zoom when we support other integration types.
    dispatch(navigateToLinkAction('/settings/integrations/native/edit/zoom/details'));
  },
}));

function mergeProps(
  stateProps: ResolveStateProps<typeof mapStateToProps>,
  dispatchProps: ResolveDispatchProps<typeof mapDispatchToProps>,
  ownProps: ConferenceDropdownBoxContainerProps,
) {
  const onConferenceTypeSelect = (conferenceType: ConferenceTypesEnum | undefined) => {
    if (!conferenceType) {
      ownProps.onConferenceTypeChange(undefined, undefined);
      return;
    }

    trackInterfaceEvent(InterfaceEventsEnum.ADD_CONFERENCE, {
      integration_type: conferenceType,
    });

    // If the conference corresponds to a calendar provider (ex. Google Hangouts, Skype Meetings),
    // create a placeholder conference that will be updated when the event is propagated to Google/Office365.
    if (isProviderConferenceType(conferenceType)) {
      ownProps.onConferenceTypeChange(conferenceType, undefined);
      return;
    }

    const integration = stateProps.conferencingIntegrations.find((i) => i.type === conferenceType);
    if (integration) {
      ownProps.onConferenceTypeChange(conferenceType, integration);
    }
  };

  return {
    ...ownProps,
    ...stateProps,
    ...dispatchProps,
    onConferenceTypeSelect,
  };
}

export const ConferenceDropdownBoxContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(ConferenceDropdownBox);
